// src/components/TariffTable.tsx
import React from 'react';
import { TariffItem } from '../types/tariff';
import { ChevronRight, Hash, FileText, Scale, ShieldCheck } from 'lucide-react';

interface TariffTableProps {
  data: TariffItem[];
  onRowClick: (hsCode: string) => void;
}

const TariffTable: React.FC<TariffTableProps> = ({ data, onRowClick }) => {
  if (data.length === 0) {
    return <p className="no-results">No matching tariffs found. Try a different search or category.</p>;
  }

  return (
    <div className="tariff-results-list">
      <style>
        {`
          .tariff-results-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 100%;
          }

          .tariff-card {
            background: white;
            border-radius: 16px;
            padding: 20px 24px;
            display: flex;
            align-items: center;
            gap: 20px;
            cursor: pointer;
            border: 1px solid #e2e8f0;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
          }

          .tariff-card::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .tariff-card:hover {
            border-color: #93c5fd;
            box-shadow: 0 8px 25px -5px rgba(59, 130, 246, 0.15), 0 4px 10px -5px rgba(59, 130, 246, 0.08);
            transform: translateY(-2px);
          }

          .tariff-card:hover::before {
            opacity: 1;
          }

          .tariff-card:active {
            transform: translateY(0);
          }

          .tariff-card-hs-badge {
            flex-shrink: 0;
            background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
            border: 1px solid #bfdbfe;
            border-radius: 12px;
            padding: 12px 16px;
            text-align: center;
            min-width: 100px;
            transition: all 0.3s ease;
          }

          .tariff-card:hover .tariff-card-hs-badge {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            border-color: #2563eb;
          }

          .tariff-card-hs-badge .hs-label {
            font-size: 0.65rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: #3b82f6;
            margin-bottom: 4px;
            transition: color 0.3s ease;
          }

          .tariff-card:hover .tariff-card-hs-badge .hs-label {
            color: rgba(255,255,255,0.8);
          }

          .tariff-card-hs-badge .hs-code {
            font-size: 1rem;
            font-weight: 700;
            color: #1e3a5f;
            font-family: 'JetBrains Mono', 'Fira Code', monospace;
            transition: color 0.3s ease;
          }

          .tariff-card:hover .tariff-card-hs-badge .hs-code {
            color: white;
          }

          .tariff-card-body {
            flex: 1;
            min-width: 0;
          }

          .tariff-card-description {
            font-size: 0.95rem;
            font-weight: 500;
            color: #1e293b;
            line-height: 1.5;
            margin-bottom: 8px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .tariff-card-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            align-items: center;
          }

          .tariff-meta-tag {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 0.78rem;
            font-weight: 500;
            color: #64748b;
            background: #f8fafc;
            padding: 4px 10px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
          }

          .tariff-meta-tag svg {
            flex-shrink: 0;
          }

          .tariff-meta-tag.category-tag {
            color: #7c3aed;
            background: #f5f3ff;
            border-color: #ddd6fe;
          }

          .tariff-meta-tag.unit-tag {
            color: #0d9488;
            background: #f0fdfa;
            border-color: #99f6e4;
          }

          .tariff-meta-tag.icl-tag {
            color: #ea580c;
            background: #fff7ed;
            border-color: #fed7aa;
          }

          .tariff-card-action {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .view-details-btn {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 10px;
            font-weight: 600;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;
            box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
          }

          .view-details-btn:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
            transform: scale(1.02);
          }

          .view-details-btn svg {
            transition: transform 0.3s ease;
          }

          .view-details-btn:hover svg {
            transform: translateX(3px);
          }

          /* Stagger animation for cards */
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .tariff-card {
            animation: slideInUp 0.4s ease forwards;
          }

          .tariff-card:nth-child(1) { animation-delay: 0.02s; }
          .tariff-card:nth-child(2) { animation-delay: 0.04s; }
          .tariff-card:nth-child(3) { animation-delay: 0.06s; }
          .tariff-card:nth-child(4) { animation-delay: 0.08s; }
          .tariff-card:nth-child(5) { animation-delay: 0.10s; }
          .tariff-card:nth-child(6) { animation-delay: 0.12s; }
          .tariff-card:nth-child(7) { animation-delay: 0.14s; }
          .tariff-card:nth-child(8) { animation-delay: 0.16s; }
          .tariff-card:nth-child(9) { animation-delay: 0.18s; }
          .tariff-card:nth-child(10) { animation-delay: 0.20s; }
          .tariff-card:nth-child(n+11) { animation-delay: 0.22s; }

          /* Mobile responsive */
          @media (max-width: 768px) {
            .tariff-card {
              flex-direction: column;
              align-items: stretch;
              padding: 16px;
              gap: 12px;
            }

            .tariff-card-hs-badge {
              min-width: unset;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 14px;
            }

            .tariff-card-hs-badge .hs-label {
              margin-bottom: 0;
            }

            .tariff-card-action {
              width: 100%;
            }

            .view-details-btn {
              width: 100%;
              justify-content: center;
              padding: 12px;
            }

            .tariff-card-meta {
              gap: 8px;
            }

            .tariff-meta-tag {
              font-size: 0.72rem;
              padding: 3px 8px;
            }
          }

          @media (max-width: 480px) {
            .tariff-card-description {
              font-size: 0.88rem;
            }
          }
        `}
      </style>

      {data.map((item, index) => {
        const hsCode = item["HS Code"]?.toString().trim();
        const description = item["Description"]?.toString() || '—';
        const category = item["HS Hdg"]?.toString() || '';
        const unit = item["Unit"]?.toString() || '';
        const icl = item["ICL SLSI"]?.toString() || '';
        const code = item["Code"]?.toString() || '';

        return (
          <div
            key={hsCode ? `${hsCode}-${index}` : `row-${index}`}
            className="tariff-card"
            onClick={() => {
              if (hsCode) onRowClick(hsCode);
            }}
            style={{ opacity: 0 }}
          >
            {/* HS Code Badge */}
            <div className="tariff-card-hs-badge">
              <div className="hs-label">HS Code</div>
              <div className="hs-code">{hsCode || '—'}</div>
            </div>

            {/* Body */}
            <div className="tariff-card-body">
              <div className="tariff-card-description">{description}</div>
              <div className="tariff-card-meta">
                {category && (
                  <span className="tariff-meta-tag category-tag">
                    <Hash size={12} />
                    Hdg: {category}
                  </span>
                )}
                {code && (
                  <span className="tariff-meta-tag">
                    <FileText size={12} />
                    {code}
                  </span>
                )}
                {unit && unit !== 'undefined' && unit !== 'null' && (
                  <span className="tariff-meta-tag unit-tag">
                    <Scale size={12} />
                    {unit}
                  </span>
                )}
                {icl && icl !== 'undefined' && icl !== 'null' && (
                  <span className="tariff-meta-tag icl-tag">
                    <ShieldCheck size={12} />
                    ICL/SLSI
                  </span>
                )}
              </div>
            </div>

            {/* Action */}
            <div className="tariff-card-action">
              <button
                className="view-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hsCode) onRowClick(hsCode);
                }}
              >
                View Details
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TariffTable;